import { Project } from '@prisma/client';
import { Decimal } from 'decimal.js';

import { VATRate } from '../db';
import { getVatRate, sum } from '../utils';

// Helper functions for better composition
const safeValue = (value: Decimal | null | undefined): Decimal =>
  value || new Decimal(0);

const applyDiscount = (amount: Decimal, discountPercentage: Decimal): Decimal =>
  amount.mul(new Decimal(1).sub(discountPercentage.div(100)));

const addVat = (amount: Decimal, vatRate: VATRate): Decimal =>
  amount.mul(new Decimal(1).add(getVatRate(vatRate)));

const calculateGrossTotal = (
  netAmount: Decimal,
  vatRate: VATRate | null,
): Decimal => {
  if (!vatRate) return new Decimal(0);
  return addVat(netAmount, vatRate);
};

// Main calculation function
export function getProjectAmounts(project: Project) {
  // Extract and safely handle project values
  const amounts = {
    furniture10Percent: safeValue(project.furniture10PercentAmount),
    faucets10Percent: safeValue(project.faucets10PercentAmount),
    delivery10Percent: safeValue(
      project.deliveryAndInstallation10PercentAmount,
    ),
    furniture: safeValue(project.furnitureAmount),
    appliance: safeValue(project.applianceAmount),
    faucets: safeValue(project.faucetsAmount),
    delivery: safeValue(project.deliveryAndInstallationAmount),
    miscellaneous: safeValue(project.miscellaneousAmount),
  };
  const architectFee = safeValue(project.architectFeeAmount);

  const ecofees = {
    furniture10Percent: safeValue(project.furniture10PercentEcofeeAmount),
    faucets10Percent: safeValue(project.faucets10PercentEcofeeAmount),
    furniture: safeValue(project.furnitureEcofeeAmount),
    appliance: safeValue(project.applianceEcofeeAmount),
    faucets: safeValue(project.faucetsEcofeeAmount),
  };

  const discounts = {
    furniture: safeValue(project.furnitureDiscountPercentage),
    appliance: safeValue(project.applianceDiscountPercentage),
  };

  const discountedAmounts = {
    furniture10Percent: applyDiscount(
      amounts.furniture10Percent,
      discounts.furniture,
    ),
    faucets10Percent: applyDiscount(
      amounts.faucets10Percent,
      discounts.furniture,
    ),
    delivery10Percent: applyDiscount(
      amounts.delivery10Percent,
      discounts.furniture,
    ),
    furniture: applyDiscount(amounts.furniture, discounts.furniture),
    appliance: applyDiscount(amounts.appliance, discounts.appliance),
    faucets: applyDiscount(amounts.faucets, discounts.furniture),
    delivery: applyDiscount(amounts.delivery, discounts.furniture),
    miscellaneous: applyDiscount(amounts.miscellaneous, discounts.furniture),
  };

  // Calculate category totals (after discount + ecofee)
  const category = {
    furniture10Percent: discountedAmounts.furniture10Percent.add(
      ecofees.furniture10Percent,
    ),
    faucets10Percent: discountedAmounts.faucets10Percent.add(
      ecofees.faucets10Percent,
    ),
    delivery10Percent: discountedAmounts.delivery10Percent,
    furniture: discountedAmounts.furniture.add(ecofees.furniture),
    appliance: discountedAmounts.appliance.add(ecofees.appliance),
    faucets: discountedAmounts.faucets.add(ecofees.faucets),
    delivery: discountedAmounts.delivery,
    miscellaneous: discountedAmounts.miscellaneous,
  };

  const gross = {
    furniture10Percent: calculateGrossTotal(
      category.furniture10Percent,
      'TEN_PERCENT',
    ),
    faucets10Percent: calculateGrossTotal(
      category.faucets10Percent,
      'TEN_PERCENT',
    ),
    delivery10Percent: calculateGrossTotal(
      category.delivery10Percent,
      'TEN_PERCENT',
    ),
    furniture: calculateGrossTotal(category.furniture, project.vatRate),
    appliance: calculateGrossTotal(category.appliance, project.vatRate),
    faucets: calculateGrossTotal(category.faucets, project.vatRate),
    delivery: calculateGrossTotal(category.delivery, project.vatRate),
    miscellaneous: calculateGrossTotal(category.miscellaneous, project.vatRate),
    architectFee: calculateGrossTotal(architectFee, project.vatRate),
  };

  return {
    gross,
    category,
    amounts,
    ecofees,
    discounts,
    discountedAmounts,
    architectFee,
  };
}

export function getInvoicingAmounts(project: Project) {
  const amounts = getProjectAmounts(project);

  const furnitureTotal10 = sum([
    amounts.discountedAmounts.furniture10Percent,
    amounts.discountedAmounts.faucets10Percent,
    amounts.discountedAmounts.delivery10Percent,
  ]);
  const ecoFurniture10 = sum([
    amounts.ecofees.furniture10Percent,
    amounts.ecofees.faucets10Percent,
  ]);

  const total10HT = sum([furnitureTotal10, ecoFurniture10]);
  const vat10 = total10HT.mul(0.1);
  const total10 = sum([total10HT, vat10]);

  const furnitureTotalHT = sum([
    amounts.discountedAmounts.furniture,
    amounts.discountedAmounts.faucets,
    amounts.discountedAmounts.delivery,
    amounts.discountedAmounts.miscellaneous,
    amounts.architectFee,
  ]);
  const applianceTotal = amounts.discountedAmounts.appliance;
  const ecoFurniture = sum([
    amounts.ecofees.furniture,
    amounts.ecofees.faucets,
  ]);
  const ecoAppliance = amounts.ecofees.appliance;
  const totalHT = sum([
    furnitureTotalHT,
    applianceTotal,
    ecoFurniture,
    ecoAppliance,
  ]);
  const vatRate = project.vatRate ? getVatRate(project.vatRate) : 0.2;
  const vat = totalHT.mul(vatRate);
  const total = sum([totalHT, vat]);

  const grandTotalHT = sum([total10HT, totalHT]);
  const grandTotalVAT = sum([vat, vat10]);
  const grandTotalTTC = grandTotalHT.add(grandTotalVAT);

  return {
    furnitureTotal10,
    ecoFurniture10,
    total10HT,
    vat10,
    total10,
    furnitureTotalHT,
    applianceTotal,
    ecoFurniture,
    ecoAppliance,
    totalHT,
    vat,
    total,
    grandTotalHT,
    grandTotalVAT,
    grandTotalTTC,
  };
}
